import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight, Plane, ShieldCheck, ClipboardCheck, Waves } from "lucide-react";
import { SERVICES, WORKFLOW_STEPS, HOW_IT_WORKS, IMAGES, BERMUDA_PARISHES } from "@/lib/site";
import { MaskedLines, Reveal, ParallaxImage } from "@/components/motion-primitives";
import Marquee from "@/components/Marquee";
import Estimator from "@/components/Estimator";
import GuestToolsPromo from "@/components/GuestToolsPromo";
import { CtaButton, CtaBand, SectionHead, usePageMeta } from "@/components/shared";

const TRUST_ITEMS = ["Airbnb Co-Hosting", "Vacation Rental Management", "Property Care", "Cleaning", "Maintenance", ...BERMUDA_PARISHES];

const CHAPTERS = [
  ["Guest Communication", "Every inquiry, message and midnight question answered promptly and professionally — never by you."],
  ["Cleaning & Turnovers", "Hotel-grade changeovers by vetted local teams, verified against a written standard every single time."],
  ["Maintenance & Inspections", "Documented inspections, coordinated trades and honest reporting before small issues become expensive ones."],
  ["A Local Presence", "Someone on the island who knows your home, answers for it and cares for it as their own."],
];

function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.12]);

  return (
    <section ref={ref} data-testid="home-hero" className="relative flex min-h-screen items-end overflow-hidden bg-navy text-ivory">
      <motion.div style={{ y, scale }} className="absolute inset-0">
        <motion.img
          src={IMAGES.hero}
          alt="Luxury Bermuda villa overlooking the ocean"
          initial={{ opacity: 0, scale: 1.08 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1.8, ease: [0.22, 1, 0.36, 1] }}
          className="h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-navy/85 via-navy/45 to-navy/15" />
        <div className="absolute inset-0 bg-gradient-to-t from-navy/80 via-transparent to-navy/25" />
      </motion.div>

      <div className="noise-overlay relative z-10 mx-auto w-full max-w-[1440px] px-6 pb-24 pt-44 lg:px-10">
        <Reveal delay={0.05} y={12}>
          <p className="mb-7 flex items-center gap-3 text-[11px] font-semibold uppercase tracking-[0.32em] text-bermuda">
            <span className="h-px w-10 bg-bermuda/70" /> Bermuda Vacation Rental Management
          </p>
        </Reveal>
        <MaskedLines
          lines={["Your Bermuda Property.", "Professionally Managed."]}
          className="max-w-5xl font-serif text-[13vw] sm:text-6xl lg:text-7xl xl:text-[84px] font-light leading-[1.02] tracking-tight"
        />
        <Reveal delay={0.6}>
          <p className="mt-8 max-w-xl text-base leading-relaxed text-ivory/85 sm:text-lg">
            From guest communication and Airbnb co-hosting to cleaning, property inspections and maintenance
            coordination — we provide the local support Bermuda vacation-rental owners need to operate with confidence.
          </p>
        </Reveal>
        <Reveal delay={0.75}>
          <div className="mt-10 flex flex-wrap gap-4">
            <CtaButton to="/assessment" variant="accent" testid="hero-assessment-btn">Get My Property Assessment</CtaButton>
            <CtaButton to="/services" variant="outline" dark testid="hero-services-btn">Explore Our Services</CtaButton>
          </div>
        </Reveal>
        <Reveal delay={1}>
          <div className="mt-16 flex items-center gap-3 text-[10px] uppercase tracking-[0.3em] text-ivory/50">
            <motion.span animate={{ y: [0, 6, 0] }} transition={{ repeat: Infinity, duration: 2.2, ease: "easeInOut" }} className="block h-8 w-px bg-ivory/40" />
            Scroll
          </div>
        </Reveal>
      </div>
    </section>
  );
}

function Manifesto() {
  return (
    <section className="mx-auto max-w-[1440px] px-6 py-24 lg:px-10 lg:py-36">
      <div className="grid gap-16 lg:grid-cols-2 lg:gap-24">
        <div>
          <SectionHead
            overline="The Owner's Truth"
            title={<>Own the Property.<br /><span className="italic text-bermuda-dark">Not the Stress.</span></>}
            copy="A profitable Bermuda vacation rental demands guest communication, cleaning, turnovers, maintenance, inspections, supplies — and someone locally available when things go wrong. We are your boots on the ground in Bermuda: the calm, capable presence between your property and everything it needs."
          />
          <div className="mt-12">
            {CHAPTERS.map(([title, copy], i) => (
              <Reveal key={title} delay={i * 0.06}>
                <div className="group flex gap-6 border-t border-navy/10 py-7 transition-colors duration-500 last:border-b hover:bg-sand-light/60">
                  <span className="font-mono text-xs text-bermuda-dark pt-1.5">{String(i + 1).padStart(2, "0")}</span>
                  <div>
                    <h3 className="font-serif text-xl sm:text-2xl text-navy">{title}</h3>
                    <p className="mt-2 max-w-md text-sm leading-relaxed text-navy/60">{copy}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
        <div className="relative">
          <ParallaxImage src={IMAGES.livingView} alt="Elegant Bermuda living room" className="h-[420px] lg:h-[560px]" />
          <Reveal delay={0.2} className="relative z-10 -mt-16 ml-auto w-[78%] bg-white p-8 shadow-2xl shadow-navy/10 lg:-mt-24">
            <p className="font-serif text-2xl leading-snug text-navy">
              "My property will be taken care of. <span className="italic text-bermuda-dark">I finally have someone reliable in Bermuda.</span>"
            </p>
            <p className="mt-4 text-[11px] uppercase tracking-[0.25em] text-navy/45">What every owner deserves to feel</p>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

function ServicesGrid() {
  return (
    <section className="bg-white">
      <div className="mx-auto max-w-[1440px] px-6 py-24 lg:px-10 lg:py-32">
        <div className="flex flex-wrap items-end justify-between gap-8">
          <SectionHead
            overline="What We Do"
            title="Every detail of your property, professionally handled."
            copy="Twelve specialist services, one accountable local partner. Begin with one — or hand over everything."
          />
          <Reveal delay={0.2}>
            <Link to="/services" data-testid="services-view-all-link" className="link-underline hidden text-[11px] font-semibold uppercase tracking-[0.2em] text-navy sm:block">
              View All Services
            </Link>
          </Reveal>
        </div>
        <div className="mt-16 grid gap-px bg-navy/10 sm:grid-cols-2 lg:grid-cols-4">
          {SERVICES.map((s, i) => (
            <Reveal key={s.slug} delay={(i % 4) * 0.07}>
              <Link
                to={s.path}
                data-testid={`service-card-${s.slug}`}
                className="group flex h-full flex-col bg-ivory p-8 transition-colors duration-500 hover:bg-navy"
              >
                <span className="font-mono text-xs text-bermuda-dark transition-colors group-hover:text-bermuda">
                  {String(i + 1).padStart(2, "0")}
                </span>
                <h3 className="mt-5 font-serif text-[22px] leading-tight text-navy transition-colors group-hover:text-ivory">
                  {s.title}
                </h3>
                <p className="mt-3 flex-1 text-[13px] leading-relaxed text-navy/55 transition-colors group-hover:text-ivory/60">
                  {s.short}
                </p>
                <span className="mt-6 flex items-center gap-2 text-[10px] font-semibold uppercase tracking-[0.2em] text-navy/50 transition-colors group-hover:text-bermuda">
                  Explore <ArrowUpRight size={13} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </span>
              </Link>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function Workflow() {
  return (
    <section className="mx-auto max-w-[1440px] px-6 py-24 lg:px-10 lg:py-32">
      <SectionHead
        overline="The Rhythm of Every Stay"
        title={<>You enjoy the income.<br /><span className="italic text-bermuda-dark">We handle the details.</span></>}
        copy="Ten coordinated moments between one booking and the next — each one owned, timed and verified by our local team."
      />
      <div className="mt-16 grid gap-px bg-navy/10 sm:grid-cols-2 lg:grid-cols-5">
        {WORKFLOW_STEPS.map(([title, copy], i) => (
          <Reveal key={title} delay={(i % 5) * 0.06} className="h-full">
            <div className="trace-card h-full bg-sand-light p-7">
              <span className="font-mono text-[11px] text-bermuda-dark">{String(i + 1).padStart(2, "0")}</span>
              <h4 className="mt-4 font-serif text-lg leading-tight text-navy">{title}</h4>
              <p className="mt-2 text-xs leading-relaxed text-navy/55">{copy}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </section>
  );
}

function Overseas() {
  return (
    <section className="bg-navy text-ivory">
      <div className="mx-auto grid max-w-[1440px] gap-14 px-6 py-24 lg:grid-cols-2 lg:gap-20 lg:px-10 lg:py-36">
        <div className="noise-overlay relative">
          <SectionHead
            dark
            overline="For Overseas & Travelling Owners"
            title={<>Own a home in Bermuda but<br /><span className="italic text-bermuda">don't live here full-time?</span></>}
            copy="A reliable local property manager means someone inspects your home, coordinates maintenance, manages cleaners, prepares for guests and reports issues — without you ever needing to be physically present. You keep ownership and visibility. We keep everything else."
          />
          <div className="mt-10 space-y-5">
            {[
              [ShieldCheck, "Documented inspections with dated photo reports, delivered wherever you are."],
              [ClipboardCheck, "One call coordinates cleaners, contractors and every detail in between."],
              [Plane, "Your home guest-ready and owner-ready — arrive to perfection, leave without worry."],
            ].map(([Icon, text], i) => (
              <Reveal key={i} delay={i * 0.08}>
                <div className="flex items-start gap-4">
                  <span className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-bermuda/40 text-bermuda">
                    <Icon size={15} strokeWidth={1.5} />
                  </span>
                  <p className="text-sm leading-relaxed text-ivory/75">{text}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.3}>
            <div className="mt-10">
              <CtaButton to="/assessment" variant="accent" testid="overseas-hands-off-btn">Become a Hands-Off Host</CtaButton>
            </div>
          </Reveal>
        </div>
        <Reveal delay={0.15} className="relative">
          <ParallaxImage src={IMAGES.modernInterior} alt="Beautifully prepared Bermuda home interior" className="h-[380px] lg:h-full min-h-[380px]" />
          <div className="absolute bottom-6 left-6 flex items-center gap-3 bg-navy/70 px-5 py-3 backdrop-blur-md">
            <Waves size={15} className="text-bermuda" />
            <span className="text-[10px] uppercase tracking-[0.25em] text-ivory/80">Cared for, island-side</span>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

function HowItWorks() {
  return (
    <section className="bg-sand-light">
      <div className="mx-auto max-w-[1440px] px-6 py-24 lg:px-10 lg:py-32">
        <SectionHead
          overline="How It Works"
          title="Five steps to a professionally managed property."
          align="center"
        />
        <div className="mt-16 grid gap-10 sm:grid-cols-2 lg:grid-cols-5">
          {HOW_IT_WORKS.map(([title, copy], i) => (
            <Reveal key={title} delay={i * 0.08}>
              <div className="relative">
                <span className="font-serif text-6xl font-light text-bermuda/50">{String(i + 1).padStart(2, "0")}</span>
                <h4 className="mt-4 font-serif text-xl text-navy">{title}</h4>
                <p className="mt-2.5 text-[13px] leading-relaxed text-navy/60">{copy}</p>
                {i < HOW_IT_WORKS.length - 1 && (
                  <span className="absolute right-0 top-8 hidden h-px w-8 bg-navy/15 lg:block" />
                )}
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

const GuidesBand = () => (
  <section className="border-y border-ivory/10 bg-navy" data-testid="guides-promo-band">
    <div className="mx-auto flex max-w-[1440px] flex-col items-start gap-3 px-6 py-5 sm:flex-row sm:items-center sm:justify-between lg:px-10">
      <p className="text-[13px] leading-relaxed text-ivory/85">
        <span className="mr-3 bg-bermuda px-2 py-0.5 text-[9px] font-bold uppercase tracking-[0.2em] text-navy">New</span>
        The Bermuda Owner Series — four digital guides written for Bermuda hosts. The complete set is <span className="font-medium text-bermuda">$79</span>, saving you $11.
      </p>
      <Link to="/guides" data-testid="guides-promo-link" className="group flex shrink-0 items-center gap-2 text-[10px] font-semibold uppercase tracking-[0.2em] text-ivory transition-colors hover:text-bermuda">
        Browse the Guides <ArrowUpRight size={13} className="transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
      </Link>
    </div>
  </section>
);

export default function Home() {
  usePageMeta(null, "Premium Bermuda vacation rental management, Airbnb co-hosting, cleaning and property care. Your Property. Professionally Managed. Locally Cared For.");
  return (
    <main>
      <Hero />
      <Marquee items={TRUST_ITEMS} />
      <GuidesBand />
      <Manifesto />
      <ServicesGrid />
      <Workflow />
      <Estimator />
      <GuestToolsPromo />
      <Overseas />
      <HowItWorks />
      <CtaBand copy="A no-obligation property assessment is the simplest first step — honest insight into your home's potential, and exactly how we would care for it." />
    </main>
  );
}
